import { ReactNode, useMemo } from 'react';
import { components, ControlProps, default as ReactSelect, Props as ReactSelectProps } from 'react-select';

import { Box } from '../../common/ui/Box';
import { Caption, CaptionProps } from '../Caption/Caption';

import { CustomClearIndicator } from './components/CustomClearIndicator';
import { CustomControl } from './components/CustomControl';
import { CustomDropdownIndicator } from './components/CustomDropdownIndicator';
import { CustomOption } from './components/CustomOption';
import { createSelectStyles } from './SelectStyles';

export interface SelectProps<Option>
    extends Pick<ReactSelectProps<Option, false>, 'value' | 'options' | 'onChange'>,
        Partial<Pick<ReactSelectProps<Option, false>, 'onBlur' | 'isClearable'>> {
    label?: string;
    placeholder?: string;
    caption?: ReactNode;
    CaptionIcon?: CaptionProps['Icon'];
    positive?: boolean;
    negative?: boolean;
    markAsRequired?: boolean;
    markAsOptional?: boolean;
    description?: string;
    disabled?: boolean;
    overrides?: ReactSelectProps<Option, false>['components'];
    styles?: ReactSelectProps<Option, false>['styles'];
    onInputChange?: ReactSelectProps<Option, false>['onInputChange'];
    filterOption?: ReactSelectProps<Option, false>['filterOption'];
}

export function Select<Option = unknown>({
    label,
    caption,
    CaptionIcon,
    placeholder = '',
    positive = false,
    negative = false,
    markAsRequired = false,
    markAsOptional = false,
    description,
    disabled = false,
    ...rest
}: SelectProps<Option>) {
    const CustomControlMemoized = useMemo(() => {
        return function CustomControlMemoizedFn({ children, ...rest }: ControlProps<Option>) {
            return (
                <CustomControl
                    {...rest}
                    label={label}
                    markAsRequired={markAsRequired}
                    markAsOptional={markAsOptional}
                    description={description}
                >
                    {children}
                </CustomControl>
            );
        };
    }, [label, markAsRequired, markAsOptional, description]);

    return (
        <Box display="flex" flexDirection="column" gap={4}>
            <ReactSelect<Option, false>
                isDisabled={disabled}
                placeholder={placeholder}
                options={rest.options}
                isSearchable={false}
                isMulti={false}
                value={rest.value}
                onChange={rest.onChange}
                onInputChange={rest.onInputChange}
                isClearable={rest.isClearable}
                styles={{
                    ...createSelectStyles({ positive, negative }),
                    ...rest.styles,
                }}
                components={{
                    ...components,
                    Option: CustomOption as any,
                    Control: CustomControlMemoized as any,
                    IndicatorSeparator: () => null,
                    DropdownIndicator: CustomDropdownIndicator as any,
                    ClearIndicator: CustomClearIndicator as any,
                    ...rest.overrides,
                }}
                onBlur={rest.onBlur}
            />
            {caption ? (
                <Caption $negative={negative} Icon={CaptionIcon}>
                    {caption}
                </Caption>
            ) : null}
        </Box>
    );
}
